import React, { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { volunteerInterests } from '@/mockData';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Volunteer = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zip: '',
    interests: []
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/volunteer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          zip_code: formData.zip || null,
          interests: formData.interests,
          text_updates: false
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Thank you for signing up. Our team will be in touch shortly.');
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (interest, checked) => {
    setFormData(prev => ({
      ...prev,
      interests: checked 
        ? [...prev.interests, interest]
        : prev.interests.filter(i => i !== interest)
    }));
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto text-center">
            <CardContent className="pt-12 pb-12">
              <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-[#0a1628] mb-4">Thank You for Signing Up!</h2>
              <p className="text-gray-700 text-lg mb-8">
                We're grateful for your support. Our team will be in touch shortly with volunteer opportunities 
                that match your interests.
              </p>
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-white">Join Our Team</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Your time and energy can make a real difference in this campaign
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Pitch */}
            <div className="mb-12 text-center">
              <img
                src="https://images.unsplash.com/photo-1560220604-1985ebfe28b1?w=1200&q=85"
                alt="Community volunteers"
                className="rounded-lg shadow-lg mb-8 w-full max-w-2xl mx-auto object-cover"
                style={{ height: '400px' }}
              />
              <h2 className="text-3xl font-bold text-[#0a1628] mb-4">
                Grassroots Campaigns Win Elections
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto">
                Whether you have five minutes or five hours, there's a way for you to help. From knocking on doors 
                to making phone calls to hosting events, every action moves us closer to victory. Join hundreds of 
                volunteers across the district who are working to bring common-sense leadership to Albany.
              </p>
            </div>

            {/* Volunteer Form */}
            <Card className="shadow-lg border border-gray-100">
              <CardContent className="p-8 lg:p-12">
                <h3 className="text-2xl font-bold text-[#0a1628] mb-6">Volunteer Sign-Up</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#0a1628] mb-2">
                        First Name *
                      </label>
                      <Input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full h-11 border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628]"
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0a1628] mb-2">
                        Last Name *
                      </label>
                      <Input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full h-11 border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628]"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0a1628] mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full h-11 border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628]"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#0a1628] mb-2">
                        Phone
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full h-11 border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628]"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0a1628] mb-2">
                        Zip Code
                      </label>
                      <Input
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        className="w-full h-11 border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628]"
                        placeholder="14614"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0a1628] mb-3">
                      I'm interested in: (select all that apply)
                    </label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {volunteerInterests.map((interest) => (
                        <div key={interest} className="flex items-center space-x-2">
                          <Checkbox
                            id={interest}
                            checked={formData.interests.includes(interest)}
                            onCheckedChange={(checked) => handleInterestChange(interest, checked)}
                            className="border-gray-300"
                          />
                          <label htmlFor={interest} className="text-gray-700 cursor-pointer text-sm">
                            {interest}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Sign Up to Volunteer'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Volunteer */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#0a1628] mb-12 text-center">
            Ways You Can Help
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border border-gray-100">
              <CardContent className="pt-8 pb-6">
                <h3 className="text-xl font-bold text-[#0a1628] mb-3">Door Knocking</h3>
                <p className="text-gray-600">
                  Connect with voters in your neighborhood and share Kalinda's vision for New York.
                </p>
              </CardContent>
            </Card>
            <Card className="border border-gray-100">
              <CardContent className="pt-8 pb-6">
                <h3 className="text-xl font-bold text-[#0a1628] mb-3">Phone Banking</h3>
                <p className="text-gray-600">
                  Make calls from home to identify supporters and turn out the vote on Election Day.
                </p>
              </CardContent>
            </Card>
            <Card className="border border-gray-100">
              <CardContent className="pt-8 pb-6">
                <h3 className="text-xl font-bold text-[#0a1628] mb-3">Event Support</h3>
                <p className="text-gray-600">
                  Help at rallies, town halls, and community events to build momentum for our campaign.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Volunteer;
